import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheck, FiStar, FiZap } from 'react-icons/fi';
import { api } from '../utils/api';
import { useRefreshListener, REFRESH_EVENTS } from '../utils/refreshEvents';
import './Pricing.css';

const Pricing = () => {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Tous les tarifs' },
    { id: 'assistance', name: 'Assistance & Communication' },
    { id: 'specifique', name: 'Services Spécifiques' },
    { id: 'coaching', name: 'Coaching & Stratégie' }
  ];

  const fetchPricing = useCallback(async () => {
    try {
      console.log('🔍 Chargement des tarifs...');
      console.log('📡 API_URL:', process.env.REACT_APP_API_URL || 'Non défini (utilise proxy)');
      const res = await api.getPricing();
      console.log('✅ Réponse API:', res);
      console.log('📦 Données reçues:', res.data);
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        setPricing(res.data);
        console.log(`✅ ${res.data.length} tarif(s) chargé(s)`);
      } else {
        console.warn('⚠️ Aucun tarif trouvé, utilisation des tarifs par défaut');
        setPricing(getDefaultPricing());
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des tarifs:', error);
      console.error('📋 Détails de l\'erreur:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      console.log('🔄 Utilisation des tarifs par défaut');
      setPricing(getDefaultPricing());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  // Écouter les événements de rafraîchissement depuis l'admin
  useRefreshListener(REFRESH_EVENTS.PRICING, fetchPricing);

  const getDefaultPricing = () => [
    { _id: '1', name: 'Administratif', category: 'assistance', type: 'carte', price: 20, unit: '€', period: '/h', description: 'Tarif à l\'heure pour les tâches administratives' },
    { _id: '2', name: 'Communication / stratégie', category: 'assistance', type: 'carte', price: 30, unit: '€', period: '/h', description: 'Tarif à l\'heure pour la communication et la stratégie (30-40€/h)' },
    { _id: '3', name: 'Essentiel', category: 'assistance', type: 'forfait', price: 150, unit: '€', period: '/mois', description: '10h/mois', features: ['mails, courriers', 'tâches de base'] },
    { _id: '4', name: 'Croissance', category: 'assistance', type: 'forfait', price: 280, unit: '€', period: '/mois', description: '20h/mois', features: ['administratif + communication simple', 'visuels/posts'], popular: true },
    { _id: '5', name: 'Premium', category: 'assistance', type: 'forfait', price: 350, unit: '€', period: '/mois', description: '40h/mois', features: ['bras droit complet', 'administratif, communication', 'suivi de projets'] },
    { _id: '6', name: 'Création Canva', category: 'specifique', type: 'service', price: 25, unit: '€', period: '/visuel', description: '25€/visuel ou 100€ les 5' },
    { _id: '7', name: 'Voix-off / podcast', category: 'specifique', type: 'service', price: 50, unit: '€', period: '/enregistrement', description: 'Enregistrement voix-off ou podcast' },
    { _id: '8', name: 'Audit + plan de communication', category: 'specifique', type: 'service', price: 150, unit: '€', period: '', description: 'Audit complet + plan de communication (150-250€)' },
    { _id: '9', name: 'Séance unique', category: 'coaching', type: 'service', price: 40, unit: '€', period: '', description: 'Séance de 1h30' },
    { _id: '10', name: 'Pack Clarté', category: 'coaching', type: 'forfait', price: 150, unit: '€', period: '/mois', description: '4 séances/mois' },
    { _id: '11', name: 'Pack Transformation', category: 'coaching', type: 'forfait', price: 300, unit: '€', period: '/mois', description: '10 séances/mois' }
  ];

  const filteredPricing = selectedCategory === 'all' 
    ? pricing 
    : pricing.filter(p => p.category === selectedCategory);

  const groupedPricing = {
    carte: filteredPricing.filter(p => p.type === 'carte'),
    forfait: filteredPricing.filter(p => p.type === 'forfait'),
    service: filteredPricing.filter(p => p.type === 'service')
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="pricing-page">
      <div className="page-header">
        <div className="page-header-background"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Nos Tarifs</h1>
            <p>Des formules adaptées à vos besoins et à votre budget</p>
          </motion.div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <motion.div
            className="category-filters"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {categories.map(cat => (
              <motion.button
                key={cat.id}
                className={`filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {cat.name}
              </motion.button>
            ))}
          </motion.div>

          {loading ? (
            <div className="loading">Chargement des tarifs...</div>
          ) : (
            <>
              {groupedPricing.carte.length > 0 && (
                <div className="pricing-section">
                  <h2 className="pricing-section-title">À la carte</h2>
                  <motion.div
                    className="pricing-grid"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                  >
                    {groupedPricing.carte.map(item => (
                      <motion.div
                        key={item._id}
                        className="pricing-card"
                        variants={itemVariants}
                        whileHover={{ y: -10, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <h3>{item.name}</h3>
                        <div className="pricing-price">
                          <span className="price-amount">{item.price}</span>
                          <span className="price-unit">{item.unit}{item.period}</span>
                        </div>
                        {item.description && <p className="pricing-description">{item.description}</p>}
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              )}

              {groupedPricing.forfait.length > 0 && (
                <div className="pricing-section">
                  <h2 className="pricing-section-title">Forfaits mensuels</h2>
                  <motion.div
                    className="pricing-grid forfait-grid"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                  >
                    {groupedPricing.forfait.map(item => (
                      <motion.div
                        key={item._id}
                        className={`pricing-card forfait-card ${item.popular ? 'popular' : ''}`}
                        variants={itemVariants}
                        whileHover={{ y: -15, scale: 1.03 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        {item.popular && (
                          <div className="popular-badge">
                            <FiStar className="star-icon" />
                            Populaire
                          </div>
                        )}
                        <div className="pricing-card-header">
                          <h3>{item.name}</h3>
                          {item.popular && <FiZap className="popular-icon" />}
                        </div>
                        <div className="pricing-price">
                          <span className="price-amount">{item.price}</span>
                          <span className="price-unit">{item.unit}{item.period}</span>
                        </div>
                        {item.description && <p className="pricing-description">{item.description}</p>}
                        {item.features && item.features.length > 0 && (
                          <ul className="pricing-features">
                            {item.features.map((feature, i) => (
                              <li key={i}>
                                <FiCheck className="check-icon" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        )}
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              )}

              {groupedPricing.service.length > 0 && (
                <div className="pricing-section">
                  <h2 className="pricing-section-title">Services spécifiques</h2>
                  <motion.div
                    className="pricing-grid"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                  >
                    {groupedPricing.service.map(item => (
                      <motion.div
                        key={item._id}
                        className="pricing-card"
                        variants={itemVariants}
                        whileHover={{ y: -10, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <h3>{item.name}</h3>
                        <div className="pricing-price">
                          <span className="price-amount">{item.price}</span>
                          <span className="price-unit">{item.unit}{item.period}</span>
                        </div>
                        {item.description && <p className="pricing-description">{item.description}</p>}
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              )}
            </>
          )}

          <motion.div
            className="pricing-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Intéressé par une de nos formules ?</h2>
            <p>Contactez-nous pour discuter de vos besoins</p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Nous contacter
              <FiArrowRight className="btn-icon" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
